"use client";

import { c as _c } from "react/compiler-runtime";
import { cx } from "@/lib/tailwind-utils";
import { MARKETING_EXPORT_DIMENSIONS, type MarketingExportLayout, type MarketingExportSize, type PairAlignment, type StudioBackdrop } from "@/types/photo-studio";

// The colour behind each panel, matching the server's backdrop_fill(): these are export pixels,
// not theme colours, so they stay fixed in dark mode.
export function backdropFill(backdrop: StudioBackdrop, brandColor: string): string {
  switch (backdrop) {
    case "WHITE":
      return "#FFFFFF";
    case "GREY":
      return "#808080";
    case "BRAND":
      return brandColor;
    default:
      return "#000000";
  }
}
export function panelSize(size: MarketingExportSize, layout: MarketingExportLayout): {
  width: number;
  height: number;
} {
  const {
    width,
    height
  } = MARKETING_EXPORT_DIMENSIONS[size];
  return layout === "SIDE_BY_SIDE" ? {
    width: Math.floor(width / 2),
    height
  } : {
    width,
    height: Math.floor(height / 2)
  };
}

// Same transform the server applies to AFTER: about the panel centre, scale, then rotate, then
// translate by dx/dy of the panel.
function alignmentTransform(alignment: PairAlignment | null, panel: {
  width: number;
  height: number;
}): string | undefined {
  if (!alignment) return undefined;
  const tx = alignment.dx * panel.width;
  const ty = alignment.dy * panel.height;
  return `translate(${tx}px, ${ty}px) rotate(${alignment.rotationDeg}deg) scale(${alignment.scale})`;
}
const PREVIEW_WIDTH = 280;
interface Props {
  beforeUrl: string | null;
  afterUrl: string | null;
  size: MarketingExportSize;
  layout: MarketingExportLayout;
  backdrop: StudioBackdrop;
  brandColor: string;
  alignment: PairAlignment | null;
  labels: {
    before: string;
    after: string;
  };
  logoUrl: string | null;
  watermarkText: string | null;
  disclaimerText: string | null;
}

// A close, client-side approximation of the exported image, scaled down to fit the dialog. The
// black pills and white text are the pixels the server draws on the export (as on the photo
// viewers), so they do not follow the theme.
export function MarketingExportPreview(t0) {
  const $ = _c(44);
  const {
    beforeUrl,
    afterUrl,
    size,
    layout,
    backdrop,
    brandColor,
    alignment,
    labels,
    logoUrl,
    watermarkText,
    disclaimerText
  } = t0;
  const dims = MARKETING_EXPORT_DIMENSIONS[size];
  const scale = PREVIEW_WIDTH / dims.width;
  const t1 = dims.height * scale;
  let frame;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== afterUrl || $[1] !== alignment || $[2] !== backdrop || $[3] !== beforeUrl || $[4] !== brandColor || $[5] !== labels.after || $[6] !== labels.before || $[7] !== layout || $[8] !== scale || $[9] !== size || $[10] !== t1) {
    frame = {
      width: PREVIEW_WIDTH,
      height: t1
    };
    let t7;
    if ($[17] !== layout || $[18] !== size) {
      t7 = panelSize(size, layout);
      $[17] = layout;
      $[18] = size;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    const panelExport = t7;
    const panel = {
      width: panelExport.width * scale,
      height: panelExport.height * scale
    };
    const fill = backdropFill(backdrop, brandColor);
    const renderPanel = (url, label, panelAlignment) => <div className="relative overflow-hidden" style={{
      width: panel.width,
      height: panel.height,
      background: fill
    }}>{url ? <img src={url} alt="" className="size-full object-cover" style={{
        transform: alignmentTransform(panelAlignment, panel),
        transformOrigin: "center"
      }} /> : <div className="flex size-full items-center justify-center text-caption text-foreground-muted">Pick a photo</div>}{label && <span className="absolute left-2 top-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] text-white">{label}</span>}</div>;
    t5 = "relative mx-auto overflow-hidden rounded-md border";
    t6 = {
      width: frame.width,
      height: frame.height,
      background: fill
    };
    const t8 = layout === "SIDE_BY_SIDE" ? "flex-row" : "flex-col";
    if ($[20] !== t8) {
      t2 = cx("flex", t8);
      $[20] = t8;
      $[21] = t2;
    } else {
      t2 = $[21];
    }
    t3 = renderPanel(beforeUrl, labels.before, null);
    t4 = renderPanel(afterUrl, labels.after, alignment);
    $[0] = afterUrl;
    $[1] = alignment;
    $[2] = backdrop;
    $[3] = beforeUrl;
    $[4] = brandColor;
    $[5] = labels.after;
    $[6] = labels.before;
    $[7] = layout;
    $[8] = scale;
    $[9] = size;
    $[10] = t1;
    $[11] = frame;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    frame = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
  }
  let t7;
  if ($[22] !== t2 || $[23] !== t3 || $[24] !== t4) {
    t7 = <div className={t2}>{t3}{t4}</div>;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== watermarkText) {
    t8 = watermarkText ? <span className="rounded-full bg-black/30 px-2 py-0.5 text-[9px] text-white/50">{watermarkText}</span> : <span />;
    $[26] = watermarkText;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== frame || $[29] !== logoUrl) {
    t9 = logoUrl && <img src={logoUrl} alt="" className="max-h-6 object-contain" style={{
      maxWidth: frame.width * 0.18
    }} />;
    $[28] = frame;
    $[29] = logoUrl;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t8 || $[32] !== t9) {
    t10 = <div className="flex items-end justify-between gap-2">{t8}{t9}</div>;
    $[31] = t8;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== disclaimerText) {
    t11 = disclaimerText && <span className="self-center rounded-full bg-black/55 px-2 py-0.5 text-[9px] text-white">{disclaimerText}</span>;
    $[34] = disclaimerText;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11) {
    t12 = <div className="pointer-events-none absolute inset-x-2 bottom-2 flex flex-col gap-1">{t10}{t11}</div>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t12 || $[40] !== t5 || $[41] !== t6 || $[42] !== t7) {
    t13 = <div className={t5} style={t6}>{t7}{t12}</div>;
    $[39] = t12;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
}